/**
 * The dispatch board's phase lanes (docs/05-admin/08 §3.1 item 3): bookings grouped by what ops
 * must do next, not by raw status.
 *
 * **Built from the spec, not a design.** The design's Dispatch board is a table, and that table is
 * still the board's default view; the owner ruled on 2026-10-26 that design-less screens are built
 * from the docs with the console's own components and restyled later.
 *
 * The lane mapping is a pure function over every `BookingStatus`: each status lands in exactly one
 * lane or is deliberately off the board (settled, cancelled and refund states have their own
 * screens). The countdown is time to the ceremony for every lane but Closing, which counts down the
 * issue window, and a ceremony under way, which shows time elapsed against the package duration.
 *
 * Where the countdown is a deadline ops can miss — the live wave's expiry while matching, the
 * payment due time while unpaid, otherwise the start a matching, unpaid or unassigned booking is
 * racing, and the issue window — it is a live `CountdownChip` that turns red inside the console's
 * urgency window, and those rows are pinned to the top of their lane. Today's confirmed and
 * in-progress ceremonies keep the plain clock: a start that arrives on schedule is not a breach.
 *
 * Each row names the devotee's area (locality, else city), never the address. `1`–`6` move focus
 * to a lane, so the board can be worked without a pointer.
 */
import { useEffect, useRef } from 'react';
import { Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { CountdownChip, Section } from '../components/kit';
import type { BoardRow } from '../lib/api/hooks/bookings';
import { ceremonyAt, clockTo, statusLabel, statusTone } from './DispatchBoard';

export const LANES = [
  { key: 'matching', title: 'Matching now', empty: 'No waves running.' },
  { key: 'payment', title: 'Awaiting payment', empty: 'Nobody is waiting to pay.' },
  { key: 'human', title: 'Needs a human', empty: 'Nothing needs a human.' },
  { key: 'today', title: 'Today, confirmed', empty: 'No confirmed ceremonies left today.' },
  { key: 'progress', title: 'In progress', empty: 'No ceremony under way.' },
  { key: 'closing', title: 'Closing', empty: 'No issue window open.' },
] as const;

export type LaneKey = (typeof LANES)[number]['key'];

/** The IST calendar day of an instant, as YYYY-MM-DD. */
export function istDay(at: Date): string {
  return new Date(at.getTime() + 330 * 60_000).toISOString().slice(0, 10);
}

/**
 * Which lane a booking is worked from, or `null` when it is off the board.
 *
 * `accepted` waits on payment (a pandit has said yes; the devotee has not paid). An expired
 * acceptance needs a human exactly as an exhausted wave does. A confirmed booking is on the board
 * only on its day: tomorrow's confirmed ceremonies need nothing from ops yet.
 */
export function laneOf(status: string, startAt: string, now: Date = new Date()): LaneKey | null {
  switch (status) {
    case 'requested':
    case 'matching':
      return 'matching';
    case 'accepted':
    case 'payment_pending':
      return 'payment';
    case 'reassignment_required':
    case 'no_provider_available':
    case 'acceptance_expired':
      return 'human';
    case 'confirmed':
      return istDay(new Date(startAt)) === istDay(now) ? 'today' : null;
    case 'provider_en_route':
    case 'in_progress':
      return 'progress';
    case 'completed':
    case 'issue_window':
      return 'closing';
    default:
      return null;
  }
}

/** Rows per lane, soonest ceremony first — "most urgent first" for the lane that needs a human. */
export function groupLanes(rows: readonly BoardRow[], now: Date = new Date()): Record<LaneKey, BoardRow[]> {
  const lanes = Object.fromEntries(LANES.map((l) => [l.key, [] as BoardRow[]])) as Record<LaneKey, BoardRow[]>;
  for (const row of rows) {
    const lane = laneOf(row.status, row.scheduled_start_at, now);
    if (lane) lanes[lane].push(row);
  }
  for (const lane of Object.values(lanes)) lane.sort((a, b) => a.scheduled_start_at.localeCompare(b.scheduled_start_at));
  return lanes;
}

/** Minutes as the board writes a span: `40m`, `2h 0m`. */
function span(minutes: number): string {
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * Closing counts down the issue window; a ceremony under way shows time elapsed against the
 * package's duration; every other lane counts to the ceremony.
 */
export function countdown(row: BoardRow, lane: LaneKey, now: number = Date.now()): string {
  if (lane === 'closing') return row.issue_window_expires_at ? `window ${clockTo(row.issue_window_expires_at, now)}` : 'window —';
  if (row.status === 'in_progress') {
    const elapsed = Math.max(0, Math.round((now - new Date(row.scheduled_start_at).getTime()) / 60000));
    return `${span(elapsed)} of ${span(row.scheduled_duration_minutes)}`;
  }
  return clockTo(row.scheduled_start_at, now);
}

/** The lanes whose countdown is a deadline, not just a start arriving on time. */
const RACING: readonly LaneKey[] = ['matching', 'payment', 'human'];

type Deadlined = Pick<BoardRow, 'status' | 'scheduled_start_at'> & {
  issue_window_expires_at?: string | null;
  payment_due_at?: string | null;
  wave_expires_at?: string | null;
  wave_number?: number | null;
  wave_limit?: number | null;
  no_show_at?: string | null;
};

/**
 * The deadline a booking is racing, if any: the issue window while it closes, the live wave's
 * expiry while it matches and the payment due time while it waits to be paid (when the row
 * carries them), the no-show sweep once the start has passed with no start code given, and
 * otherwise the start a matching,
 * unpaid or unassigned booking must be covered by. Shared by the lanes and the booking detail's
 * urgency banner, so the two never disagree about what is urgent.
 */
export function racingDeadline(row: Deadlined, now: Date = new Date()): { until: string; label: string } | null {
  const lane = laneOf(row.status, row.scheduled_start_at, now);
  if (lane === 'closing') return row.issue_window_expires_at ? { until: row.issue_window_expires_at, label: 'window' } : null;
  if (lane === 'matching' && row.wave_expires_at) {
    const which = row.wave_number && row.wave_limit ? ` ${row.wave_number}/${row.wave_limit}` : '';
    return { until: row.wave_expires_at, label: `wave${which}` };
  }
  if (lane === 'payment' && row.payment_due_at) return { until: row.payment_due_at, label: 'payment due' };
  if (row.no_show_at && new Date(row.scheduled_start_at) <= now) return { until: row.no_show_at, label: 'no-show' };
  if (lane !== null && RACING.includes(lane)) return { until: row.scheduled_start_at, label: 'start' };
  return null;
}

/**
 * The lane's rows with those inside the urgency window of their deadline (or past it) pinned
 * first, each half keeping its soonest-first order. With no window read nothing is pinned.
 */
export function pinUrgent(rows: readonly BoardRow[], now: Date, urgentWithinMinutes: number | undefined): BoardRow[] {
  if (urgentWithinMinutes === undefined) return [...rows];
  const edge = now.getTime() + urgentWithinMinutes * 60_000;
  const urgent = (row: BoardRow) => {
    const deadline = racingDeadline(row, now);
    return deadline !== null && new Date(deadline.until).getTime() <= edge;
  };
  return [...rows.filter(urgent), ...rows.filter((r) => !urgent(r))];
}

/** Where the ceremony is, at the grain the board may show: the locality, else the city. */
export function areaOf(row: Pick<BoardRow, 'venue_area'>): string {
  return row.venue_area?.locality_name ?? row.venue_area?.city_name ?? '—';
}

function liveClock(row: BoardRow, lane: LaneKey, now: number, ticking: boolean, urgentWithinMinutes: number | undefined) {
  const deadline = racingDeadline(row, new Date(now));
  if (!deadline) return countdown(row, lane, now);
  return (
    <CountdownChip
      until={deadline.until}
      label={deadline.label}
      urgentWithinMinutes={urgentWithinMinutes}
      now={ticking ? undefined : now}
    />
  );
}

export function PhaseLanes({
  rows,
  total,
  onOpen,
  now,
  urgentWithinMinutes,
}: {
  rows: readonly BoardRow[];
  total: number;
  onOpen: (id: number) => void;
  now?: Date;
  urgentWithinMinutes?: number;
}) {
  const at = now ?? new Date();
  const lanes = groupLanes(rows, at);
  const laneRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const jump = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
      const index = Number(event.key) - 1;
      const lane = laneRefs.current[index];
      if (!lane) return;
      event.preventDefault();
      lane.focus();
    };
    document.addEventListener('keydown', jump);
    return () => document.removeEventListener('keydown', jump);
  }, []);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      {total > rows.length && (
        <Note>
          Showing the soonest {rows.length} of {total} bookings in these states — narrow by city to see the rest.
        </Note>
      )}
      {LANES.map((lane, index) => {
        const mine = pinUrgent(lanes[lane.key], at, urgentWithinMinutes);
        return (
          <div
            key={lane.key}
            ref={(el) => {
              laneRefs.current[index] = el;
            }}
            tabIndex={-1}
            aria-label={`${lane.title} (press ${index + 1})`}
          >
            <Section title={lane.title} note={`${mine.length}`} pad={mine.length === 0}>
              {mine.length === 0 ? (
                <span style={{ color: 'var(--c-success-text)', fontSize: 'var(--fs-admin-colhead)' }}>✓ {lane.empty}</span>
              ) : (
                <DataTable
                  density="compact"
                  onRowClick={(row) => onOpen(Number(row.id))}
                  columns={[
                    { key: 'code', label: 'Booking', mono: true },
                    { key: 'when', label: 'Ceremony' },
                    { key: 'mode', label: 'Service' },
                    { key: 'area', label: 'Area' },
                    { key: 'status', label: 'State' },
                    { key: 'clock', label: 'Countdown', align: 'right', mono: true },
                  ]}
                  rows={mine.map((b) => ({
                    id: b.id,
                    code: b.public_code,
                    when: ceremonyAt(b.scheduled_start_at),
                    mode: statusLabel(b.service_mode),
                    area: areaOf(b),
                    status: <StatusChip tone={statusTone(b.status)} label={statusLabel(b.status)} />,
                    clock: liveClock(b, lane.key, at.getTime(), now === undefined, urgentWithinMinutes),
                  }))}
                />
              )}
            </Section>
          </div>
        );
      })}
    </div>
  );
}
